import jilDirectories from 'api/jil/jilDirectories';
import DirectoryUser from 'models/directory/DirectoryUser';
import feature from 'services/feature';
import MemberList from 'services/member/MemberList';

import {DIRECTORY_USER_LIST_CACHE_ID} from './DirectoryUserListConstants';

/**
 * UserList for directory users.
 */
class DirectoryUserList extends MemberList {
  /**
   * @param {Object} options - Top level wrapper
   * @param {string} options.directoryId - Id of the directory the users belong
   *     to.
   */
  constructor({directoryId, ...options}) {
    super({
      // Because of the large number of directory users, we let the cache timeout.
      // When users are removed, via save(), the cache will be explicitly cleared.
      // Removal is the only operation supported within Admin Console.
      // While not ideal, admins know to refresh the page/list if they externally add users.
      cacheClearingEvents: [],
      itemClassRef: DirectoryUser,
      modelCacheId: DIRECTORY_USER_LIST_CACHE_ID,
      refreshResource: jilDirectories.getDirectoryUsers,
      saveResource: jilDirectories.patchDirectoryUsers,
      ...options,
    });
    this.directoryId = directoryId;
  }

  /**
   * @description overidden key method to be used when we fetch models from modelCache.
   * @deprecated Remove with temp_src2_cache
   *
   * @returns {String} key formed from local params and JilModelList params.
   */
  getKey() {
    if (feature.isEnabled('temp_src2_cache')) {
      return super.getKey();
    }
    return super.getKey(this.getQueryParams());
  }

  /**
   * @description Get params for api call to fetch list.
   *
   * @returns {Object} params to be passed to query
   */
  getQueryParams() {
    return feature.isEnabled('temp_src2_cache')
      ? {
          ...super.getQueryParams(),
          directoryId: this.directoryId,
        }
      : {directoryId: this.directoryId, orgId: this.orgId};
  }

  /**
   * @description Method to save changes to the directory user list.
   *     Overriding userList.save because we need to provide the directoryId to
   *     the resource performing the API call so the generated yurel includes the
   *     directoryId.
   * @returns {Promise} promise - resolved after the API call is executed
   */
  async save() {
    await super.save(
      {
        directoryId: this.directoryId,
        ...(feature.isEnabled('temp_src2_cache') ? {} : {orgId: this.orgId}),
      },
      {refresh: false}
    );

    // This list only clears its cache on removal of directory users.
    // Listening to MEMBER_EVENT.UPDATE would cause the cache to be cleared whenever any of the user
    // lists are modified.
    this.clearCache();

    return this;
  }
}

export default DirectoryUserList;
